import { useCallback } from 'react'
import { ENUM_COMPONENTSHAREDCTA_KIND } from 'src/strapi/queries'

export type StrapiButton = {
  label: string
  kind: ENUM_COMPONENTSHAREDCTA_KIND | null
  url?: string | null
  route?: string | null
  action?: string | null
  params?: Record<string, string> | null
}

export type Cta = {
  label: string
  kind: 'external_url' | 'internal_url' | 'app_route' | 'app_action'
  url?: string
  route?: string
  action?: string
  params?: Record<string, string>
}

type UseCtaOptions = {
  openApp: (route?: string, params?: Record<string, string>) => void
  handleSendFreeCard: () => void
  sponsorId?: string
  actionOverrides?: Record<string, () => void>
}

const appendQuery = (
  rawUrl: string,
  params: Record<string, string | undefined>,
) => {
  if (!rawUrl) return rawUrl
  const base = rawUrl.startsWith('http') ? undefined : window.location.origin
  const u = new URL(rawUrl, base)
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== '') u.searchParams.set(k, v)
  })
  return u.toString()
}

export const normalizeCtaFromStrapi = (
  btn?: StrapiButton | null,
): Cta | null => {
  if (!btn || !btn.kind) return null
  switch (btn.kind) {
    case ENUM_COMPONENTSHAREDCTA_KIND.external_url:
      return {
        label: btn.label,
        kind: 'external_url',
        url: btn.url ?? '',
        params: btn.params ?? {},
      }
    case ENUM_COMPONENTSHAREDCTA_KIND.internal_url:
      return {
        label: btn.label,
        kind: 'internal_url',
        url: btn.url ?? '',
        params: btn.params ?? {},
      }
    case ENUM_COMPONENTSHAREDCTA_KIND.app_route:
      return {
        label: btn.label,
        kind: 'app_route',
        route: btn.route ?? '',
        params: btn.params ?? {},
      }
    case ENUM_COMPONENTSHAREDCTA_KIND.app_action:
      return {
        label: btn.label,
        kind: 'app_action',
        action: btn.action ?? '',
        params: btn.params ?? {},
      }
    default:
      return null
  }
}

export function useCta({
  openApp,
  handleSendFreeCard,
  sponsorId,
  actionOverrides = {},
}: UseCtaOptions) {
  const withSponsor = useCallback(
    (url: string, params?: Record<string, string>) =>
      appendQuery(url, {
        ...(params ?? {}),
        ...(sponsorId ? { sponsor: sponsorId } : {}),
      }),
    [sponsorId],
  )

  const toOnClick = useCallback(
    (cta: Cta) => {
      switch (cta.kind) {
        case 'external_url':
          return () => {
            const href = withSponsor(cta.url ?? '', cta.params)
            window.open(href, '_blank', 'noopener,noreferrer')
          }
        case 'internal_url':
          return () => {
            const href = withSponsor(cta.url ?? '', cta.params)
            window.location.assign(href)
          }
        case 'app_route':
          return () => {
            const params = { ...(cta.params ?? {}) }
            openApp(cta.route ?? '', params)
          }
        case 'app_action': {
          if (cta.action && actionOverrides[cta.action]) {
            return actionOverrides[cta.action]
          }
          if (cta.action === 'SEND_FREE_CARD') return handleSendFreeCard
          if (cta.action === 'OPEN_PRICING')
            return () => window.location.assign('/app/pricing#packages')
          return () => {}
        }
        default:
          return () => {}
      }
    },
    [openApp, handleSendFreeCard, withSponsor, actionOverrides],
  )

  return { toOnClick, normalizeCtaFromStrapi }
}
